import * as Yup from "yup";
import { Modal } from "flowbite-react";
import { useParams } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";
import { useCallback, useMemo, useState } from "react";
import { CHARGE_TYPE } from "../../../../../../../types/charge.type";
import { useBillingForm } from "../form/customHook/useBillingForm";
import InputFormik from "../../../../../../../common/components/form/InputFormik";
import { maskLicensePlate } from "../../../../../../../common/helpers/mask.helpers";
import ComboBoxFormik from "../../../../../../../common/components/form/ComboBoxFormik";
import { createContractFines } from "../../../../../../../common/services/payments.api";
import TextAreaFormik from "../../../../../../../common/components/form/TextAreaFormik";
import InputMoney from "../../../../../../../common/components/form/CurrencyInputFormik";
import { useToastContext } from "../../../../../../../common/components/ui/toast/toast.context";
import ButtonWithLoader from "../../../../../../../common/components/ui/loading/ButtonLoading";
import { InstallmentOption, useCalculateInstallmentOptions } from "../../../../../helpers/calculationUtils";
import { BILLING_TYPE } from "../../../../../../../types/payment.type";


type Props = {
  open: boolean;
  onClosed: () => void;
}

const validationSchema = Yup.object().shape({
  value: Yup.string()
    .required("Valor da cobrança é obrigatório")
    .test(
      'greater-than-zero',
      "O valor deve ser maior que 0,00",
      (value) => {
        if (!value) return false;

        // Remove possíveis caracteres de formatação como vírgulas e pontos
        const numericValue = parseFloat(value.replace(/\./g, '').replace(',', '.'));
        return numericValue > 0;
      }
    ),
  licensePlate: Yup.string().required("Placa do veículo é obrigatória"),
  optionsBillingCharge: Yup.string().required("Tipo de cobrança é obrigatório"),
  installmentOption: Yup.string().required("Opção de parcelamento é obrigatória"),
  nextDueDate: Yup.string()
    .required("Data de cobrança é obrigatória")
    .test(
      'future-date',
      "A data não pode ser anterior à data atual",
      (value) => {
        if (!value) return false;

        // Cria a data atual sem horas (00:00:00)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Converte a data do input para objeto Date
        const parts = value.split('/');
        const inputDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        inputDate.setHours(0, 0, 0, 0);

        // Comparação segura usando timestamp
        return inputDate.getTime() >= today.getTime();
      }
    ),
  description: Yup.string().required("Descrição da cobrança é obrigatória"),
});
export function ModalContractFine(props: Props) {
  const [, setIsSubmitting] = useState(false);
  const { notifyError, notifySuccess } = useToastContext();
  const { renterId } = useParams()

  const handleCloseModal = () => { props.onClosed && props.onClosed() };

  const { optionsBillingCharge } = useBillingForm();


  const initialValues = {
    renterId: renterId || "",
    licensePlate: "",
    value: 0,
    installmentOption: 0,
    nextDueDate: "",
    optionsBillingCharge: "",
    description: "",
    reference: CHARGE_TYPE.CONTRACTFINE,
    paymentType: BILLING_TYPE.UNDEFINED
  }

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);

        const result = await createContractFines(values);


        if (!result.ok) {
          notifyError('Erro ao criar cobrança');
        }

        notifySuccess('Cobrança criada com sucesso!');

      } finally {
        setIsSubmitting(false);
      }
    }
  })

  const calculateInstallmentOptions = useCalculateInstallmentOptions();
  const installmentOptions: InstallmentOption[] = useMemo(() => {
    return calculateInstallmentOptions(formik.values.value, 20);
  }, [formik.values.value, calculateInstallmentOptions]);



  const handleOptionsBillingCharge = useCallback((option) => {
    formik.setFieldValue('optionsBillingCharge', option);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.optionsBillingCharge]);

  return (
    <Modal
      size="3xl"
      show={props.open}
      onClose={() => handleCloseModal()}
    >
      <Modal.Header className="text-black">
        <div className="flex items-baseline justify-center bg-gray-100">
          <h2 className="ml-2 text-black">Adicionar Cobrança</h2>
        </div>
      </Modal.Header>
      <Modal.Body className="dark:text-black">
        <FormikProvider value={formik}>
          <div className="grid grid-cols-2 gap-2">
            <InputMoney
              label="valor da cobrança"
              name="value"
              required={true}
            />

            <ComboBoxFormik
              name="optionsBillingCharge"
              label="Tipo de cobrança"
              options={[{ label: "-", value: "-" }, ...optionsBillingCharge]}
              onChange={handleOptionsBillingCharge}
              required={true}
            />

            <InputFormik
              label="Data de cobrança da primeira parcela"
              name={'nextDueDate'}
              type="date"
              required={true}
            />

            <InputFormik
              label="Placa do veículo"
              name='licensePlate'
              required={true}
              mask={maskLicensePlate}
            />

            <ComboBoxFormik
              name="installmentOption"
              label="Escolha a opção de parcelamento:"
              options={installmentOptions}
              required={true}
            />
            <ComboBoxFormik
              label="Tipo de pagamento"
              name="paymentType"
              options={[
                { label: '-', value: "" },
                { label: 'PIX', value: BILLING_TYPE.PIX },
                { label: 'CREDITO', value: BILLING_TYPE.CREDIT_CARD },
                { label: 'BOLETO', value: BILLING_TYPE.BOLETO }
              ]}
            />
          </div>

          <TextAreaFormik
            className="mt-4"
            label="Descrição da cobrança"
            name={'description'}
            required={true}
            placeholder="Descreva a cobrança"
          />
          <form onSubmit={formik.handleSubmit}>
            <div className="flex justify-end col-span-5 mt-10">
              <ButtonWithLoader type="submit" isLoading={formik.isSubmitting} disabled={!formik.isValid} onClick={formik.handleSubmit}>
                Enviar
              </ButtonWithLoader>
            </div>
          </form>
        </FormikProvider>
      </Modal.Body>
    </Modal>
  )
}